/**
 * Partner Slice
 * Manages partner state and live status
 */

import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { storefrontAPI } from '../../utils/api';
import { PartnerLiveResponse, PartnerState } from '../types';
import { signIn } from './auth-slice';

// Initial State
export const partnerInitialState: PartnerState = {
    partnerId: '',
    prodAllowed: false,
    isLive: false,
    isLoading: false,
    error: null,
    disabledData: null,
};

// Async Thunks

/**
 * Check if partner is live
 */
export const checkIsPartnerLive = createAsyncThunk<PartnerLiveResponse, void, { rejectValue: string }>(
    'partner/checkIsPartnerLive',
    async (_, { rejectWithValue }) => {
        try {
            const response = await storefrontAPI.isPartnerLive();
            if (response && 'error' in response && response.error) {
                return rejectWithValue(response.message || 'Failed to check partner status');
            }
            return response;
        } catch {
            return rejectWithValue('Failed to check partner status');
        }
    },
);

function applyPartnerStatus(state: PartnerState, data: PartnerLiveResponse): void {
    state.prodAllowed = data.prod_allowed;
    state.isLive = data.prod_allowed;
    if (!data.prod_allowed) {
        state.disabledData = {
            message: data.message || 'Partner is not available',
            brandImage: data.brand_image,
            moreInfoMessage: data.more_info_message,
            moreInfoLink: data.more_info_link,
            contact: data.contact,
        };
    } else {
        state.disabledData = null;
    }
}

// Slice
export const partnerSlice = createSlice({
    name: 'partner',
    initialState: partnerInitialState,
    reducers: {
        setPartnerLive(state, action: PayloadAction<boolean>) {
            state.isLive = action.payload;
        },
        setPartnerId(state, action: PayloadAction<string>) {
            state.partnerId = action.payload;
        },
        resetPartner(state) {
            Object.assign(state, partnerInitialState);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(checkIsPartnerLive.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(checkIsPartnerLive.fulfilled, (state, action) => {
                state.isLoading = false;
                applyPartnerStatus(state, action.payload);
            })
            .addCase(checkIsPartnerLive.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        builder.addCase(signIn.fulfilled, (state, action) => {
            if (action.payload.partner) {
                applyPartnerStatus(state, action.payload.partner);
            }
        });
    },
});

// Export actions
export const { setPartnerLive, setPartnerId, resetPartner } = partnerSlice.actions;

// Selectors
type RootState = { partner: PartnerState };

export const selectPartner = (state: RootState): PartnerState => state.partner;
export const selectIsPartnerLive = createSelector(selectPartner, (partner) => partner.isLive);
export const selectPartnerDisabledData = createSelector(selectPartner, (partner) => partner.disabledData);
export const selectIsPartnerLoading = createSelector(selectPartner, (partner) => partner.isLoading);
export const selectPartnerError = createSelector(selectPartner, (partner) => partner.error);

export default partnerSlice.reducer;
